import React, { useState, useEffect } from "react";

const MaterialSubjectLinkForm = ({ material, subjects, subjectContent, onSubmit, onClose }) => {
  const [linkedSubjects, setLinkedSubjects] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Initialize with currently linked subjects
  useEffect(() => {
    if (material && subjects) {
      // Check reverse relationship: find subjects that are in material's subjects array
      const currentlyLinked = subjects
        .filter(s => 
          (material.subjects || []).some(subjId => (subjId._id || subjId) === s._id)
        )
        .map(subject => ({
          subjectId: subject._id,
          subject: subject,
          isLinked: true
        }));
      
      setLinkedSubjects(currentlyLinked);
    }
  }, [material, subjects]);

  // Filter subjects: only show those with at least one PDF uploaded
  // But also include currently linked subjects even if they don't have PDFs (so they can be unlinked)
  const subjectsWithPdfs = subjects.filter(subject => {
    const pdfs = (subjectContent && subjectContent[subject._id]?.pdfs) || [];
    const isCurrentlyLinked = linkedSubjects.some(ls => ls.subjectId === subject._id);
    return pdfs.length > 0 || isCurrentlyLinked;
  });

  // Filter subjects based on search
  const filteredSubjects = subjectsWithPdfs.filter(subject =>
    subject.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    subject.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Toggle subject link
  const toggleSubjectLink = (subject) => {
    setLinkedSubjects(prev => {
      const existing = prev.find(s => s.subjectId === subject._id);
      if (existing) {
        // Unlink
        return prev.filter(s => s.subjectId !== subject._id);
      } else {
        // Link
        return [...prev, {
          subjectId: subject._id,
          subject: subject,
          isLinked: false
        }];
      }
    });
  };

  // Handle submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const result = await onSubmit(material._id, linkedSubjects);
      if (result?.success) {
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content-form bg-white rounded-16 shadow-5" style={{ maxWidth: '900px', maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <div>
            <h4 className="modal-title">Link Subjects to {material?.title}</h4>
            <p className="text-13 text-light-1 mt-5">
              Select subjects with PDFs to link to this material
            </p>
          </div>
          <button onClick={onClose} className="modal-close-btn">
            <i className="icon-close"></i>
          </button>
        </div>
        
        <div className="modal-body" style={{ overflow: 'auto', flex: 1 }}>
          <form onSubmit={handleSubmit} className="content-form">
            {/* Search */}
            <div className="form-group mb-20">
              <input
                type="text"
                placeholder="Search subjects..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input"
                style={{ padding: '10px 15px' }}
              />
              <p className="text-11 text-light-2 mt-8" style={{ fontStyle: 'italic' }}>
                <i className="icon-info-circle mr-5"></i>
                Only subjects with PDFs uploaded will be shown here
              </p>
            </div>

            {/* Subjects List */}
            <div className="subjects-link-list" style={{ maxHeight: '400px', overflow: 'auto' }}>
              {filteredSubjects.length === 0 ? (
                <div className="text-center py-30 text-13 text-light-2">
                  <i className="icon-search text-30 mb-10"></i>
                  <p>
                    {searchTerm 
                      ? 'No subjects with PDFs found matching your search'
                      : 'No subjects with PDFs uploaded yet'
                    }
                  </p>
                  {!searchTerm && (
                    <p className="text-11 text-light-3 mt-10">
                      Upload PDFs to subjects first before linking them to materials
                    </p>
                  )}
                </div>
              ) : (
                filteredSubjects.map((subject) => {
                  const linkedItem = linkedSubjects.find(s => s.subjectId === subject._id);
                  const isLinked = !!linkedItem;

                  return (
                    <div key={subject._id} className="subject-link-item bg-light-5 rounded-8 p-15 mb-15 border border-light-3">
                      {/* Subject Header */}
                      <div className="d-flex items-center justify-between">
                        <div className="d-flex items-center flex-1">
                          <label className="checkbox-label" style={{ margin: 0, cursor: 'pointer', flex: 1 }}>
                            <input
                              type="checkbox"
                              checked={isLinked}
                              onChange={() => toggleSubjectLink(subject)}
                              className="form-checkbox mr-10"
                            />
                            <div className="d-flex items-center">
                              <div 
                                className="size-35 d-flex justify-center items-center rounded-6 mr-10"
                                style={{backgroundColor: subject.color + '20', color: subject.color}}
                              >
                                <i className={`text-14 ${subject.icon || 'icon-book'}`}></i>
                              </div>
                              <div>
                                <div className="text-14 fw-500">{subject.title}</div>
                                <div className="text-12 text-light-2">{subject.description || 'No description'}</div>
                              </div>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="modal-footer mt-20">
              <button type="button" onClick={onClose} className="btn-cancel">
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting || linkedSubjects.length === 0}
                className="btn-primary bg-purple"
              >
                {submitting ? 'Saving...' : `Link ${linkedSubjects.length} Subject${linkedSubjects.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default MaterialSubjectLinkForm;
